import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { t } from 'i18next';

import { UpdateEventBannerRequest, UpdateEventRequest } from '@/api/services/api/generated/data-contracts';
import { eventController } from '@/api/services/api/routes';
import { hasTextContent } from '@/lib/utils';
import { toast } from '../components/toast';
import { userKeys } from './use-user-events';

export const eventKeys = {
  all: ['events'] as const,
  event: (id: string) => [...eventKeys.all, id],
  contract: (id: string) => [...eventKeys.all, id, 'contract'],
};

export const useEventMutation = () => {
  return useMutation({
    mutationFn: (data: unknown) => eventController.create(data),
    onError: () => {
      toast({
        description: t('user-not-allowed'),
        title: t('error'),
        status: 'danger',
      });
    },
  });
};

export const useEvent = (eventId: string) => {
  return useSuspenseQuery({
    queryFn: () => eventController.getEventById({ urlParams: { eventId } }),
    queryKey: eventKeys.event(eventId),
    select: ({ data }) => data,
  });
};

export const useDeleteEvent = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => eventController.delete({ urlParams: { eventId } }),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};
export const useToggleEventPrivacy = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => eventController.togglePrivacy(null, { urlParams: { eventId } }),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: userKeys.lists() });
    },
  });
};
export const useRequiredFields = (eventId: string) => {
  const { data } = useQuery({
    queryFn: () => eventController.getEventById({ urlParams: { eventId } }),
    queryKey: eventKeys.event(eventId),
    select: ({ data }) => {
      const { name, description, categories, bannerUrl, organizationEmail, startDate, endDate } = data;
      const hasDescription = hasTextContent(description);
      return {
        fieldsMissing: [
          { label: t('event-name'), value: name },
          {
            label: t('description'),
            value: hasDescription,
          },
          {
            label: t('category'),
            value: categories.length > 0,
          },
          {
            label: t('support-email'),
            value: organizationEmail,
          },
          {
            label: t('start-date'),
            value: startDate,
          },
          {
            label: t('end-date'),
            value: endDate,
          },
          { label: t('event-image'), value: bannerUrl },
        ],
        hasRequiredFields: !!name && hasDescription && categories.length > 0 && !!bannerUrl && !!organizationEmail,
      };
    },
  });
  return data;
};
export const usePublishEvent = (eventId: string) => {
  return useMutation({
    mutationFn: () => eventController.publish(undefined, { urlParams: { eventId } }),
  });
};
export const useUnpublishEvent = (eventId: string) => {
  return useMutation({
    mutationFn: () => eventController.unpublish(undefined, { urlParams: { eventId } }),
  });
};
export const useUpdateBanner = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateEventBannerRequest) => {
      const formData = new FormData();
      if (data.file) {
        formData.append('banner', data.file);
      }
      return eventController.updateBanner(formData, {
        urlParams: { eventId },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: eventKeys.event(eventId) }),
        queryClient.invalidateQueries({ queryKey: userKeys.lists() }),
      ]);
    },
  });
};
export const useInitiateContractSession = (eventId: string) => {
  return useMutation({
    mutationFn: () => eventController.initiateContractSession(undefined, { urlParams: { eventId } }),
  });
};

export const useEventContract = (eventId: string, options?: { refetchInterval?: number | false }) => {
  return useQuery({
    queryFn: () => eventController.getEventContract({ urlParams: { eventId } }),
    queryKey: eventKeys.contract(eventId),
    select: ({ data }) => data,
    ...options,
  });
};

export const useConfirmContract = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => eventController.confirmContract(undefined, { urlParams: { eventId } }),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: eventKeys.contract(eventId) });
    },
  });
};

export const useRefuseContract = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => eventController.refuseContract(undefined, { urlParams: { eventId } }),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: eventKeys.contract(eventId) });
    },
  });
};

export const useUpdateEvent = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['update-event'],
    mutationFn: (data: UpdateEventRequest) => eventController.update(data, { urlParams: { eventId } }),
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: eventKeys.event(eventId) }),
        queryClient.invalidateQueries({ queryKey: userKeys.lists() }),
      ]);
    },
  });
};

export const useUploadEventImage = (eventId: string) => {
  return useMutation({
    mutationFn: (files: File[]) => {
      const formData = new FormData();
      files.forEach((file) => formData.append('images', file));
      return eventController.uploadImage(formData, {
        urlParams: { eventId },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
  });
};

export const useDeleteEventImage = (eventId: string) => {
  return useMutation({
    mutationFn: (fileName: string) => eventController.deleteImage({ urlParams: { eventId, fileName } }),
  });
};
